/**
 * Notes Tab Component
 *
 * Internal notes for super admins with pinning support.
 */
import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Skeleton,
} from '@respectly/ui';
import { Plus, Pin, PinOff, Trash2, StickyNote, User, Calendar } from 'lucide-react';
import { useAccountsStore } from '../../../stores/useAccountsStore';

interface NotesTabProps {
  accountId: string;
}

export function NotesTab({ accountId }: NotesTabProps) {
  const {
    accountNotes,
    isLoadingNotes,
    isPerformingAction,
    loadAccountNotes,
    addAccountNote,
    deleteAccountNote,
    toggleNotePin,
  } = useAccountsStore();

  const [newNoteContent, setNewNoteContent] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    loadAccountNotes(accountId);
  }, [accountId, loadAccountNotes]);

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;

    const success = await addAccountNote(accountId, {
      content: newNoteContent.trim(),
      isPinned: false,
    });

    if (success) {
      setNewNoteContent('');
      setIsAdding(false);
    }
  };

  const handleTogglePin = async (noteId: string, isPinned: boolean) => {
    await toggleNotePin(accountId, noteId, !isPinned);
  };

  const handleDelete = async (noteId: string) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      await deleteAccountNote(accountId, noteId);
    }
  };

  // Sort notes: pinned first, then by date
  const sortedNotes = [...accountNotes].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  if (isLoadingNotes) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Internal Notes</CardTitle>
          <Skeleton className="h-9 w-24" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-lg border p-4">
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-3 w-1/4" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">
          Internal Notes ({accountNotes.length})
        </CardTitle>
        {!isAdding && (
          <Button size="sm" onClick={() => setIsAdding(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add Note
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {/* Add Note Form */}
        {isAdding && (
          <div className="mb-6 rounded-lg border bg-slate-50 p-4 dark:bg-slate-800/50">
            <textarea
              placeholder="Write your note..."
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              className="w-full rounded-md border bg-white p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-900 dark:border-slate-700"
              rows={4}
              autoFocus
            />
            <div className="mt-3 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsAdding(false);
                  setNewNoteContent('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleAddNote}
                disabled={!newNoteContent.trim() || isPerformingAction}
              >
                {isPerformingAction ? 'Saving...' : 'Save Note'}
              </Button>
            </div>
          </div>
        )}

        {/* Notes List */}
        {sortedNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="rounded-2xl bg-slate-100 p-4 dark:bg-slate-800">
              <StickyNote className="h-10 w-10 text-slate-400" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
              No notes yet
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Add internal notes about this account for your team.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedNotes.map((note) => (
              <div
                key={note.id}
                className={`rounded-lg border p-4 ${
                  note.isPinned
                    ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    {note.author && (
                      <span className="flex items-center gap-1">
                        <User className="h-3.5 w-3.5" />
                        {note.author.firstName} {note.author.lastName}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(note.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleTogglePin(note.id, note.isPinned)}
                    >
                      {note.isPinned ? (
                        <PinOff className="h-3.5 w-3.5 text-amber-600" />
                      ) : (
                        <Pin className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-500 hover:text-red-600"
                      onClick={() => handleDelete(note.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Content */}
                <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {note.content}
                </p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
